import React, { useState, useRef, useEffect } from 'react';
import ImageCropper from './ImageCropper';
import './ImageViewModal.css';

const ImageViewModal = ({ 
  isOpen, 
  onClose, 
  imageUrl, 
  imageType, // 'profile' or 'banner'
  onChangeImage,
  onRemoveImage,
  isUploading = false,
  uploadProgress = 0,
  initialFile = null // File to automatically process when modal opens
}) => {
  const fileInputRef = useRef(null);
  const currentImageRef = useRef(null); // Ref to preserve current image during state updates
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [hasProcessedInitialFile, setHasProcessedInitialFile] = useState(false);
  const [imageKey, setImageKey] = useState(0); // Unique key to force re-render
  const [isLoadingNewImage, setIsLoadingNewImage] = useState(false); // Track when loading new image
  
  // Keep ref in sync with imageToCrop
  useEffect(() => {
    if (imageToCrop) {
      currentImageRef.current = imageToCrop;
    }
  }, [imageToCrop]);

  // Process initial file when modal opens (only once, and only if no manual selection has been made)
  useEffect(() => {
    // Only process initialFile if:
    // 1. Modal is open
    // 2. initialFile exists
    // 3. We haven't processed it yet
    // 4. We don't already have an image to crop (to prevent overwriting manual selections)
    if (isOpen && initialFile && !hasProcessedInitialFile && !imageToCrop) {
      // Validate file type
      if (!initialFile.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (initialFile.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Create preview and show cropper
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target.result;
        setImageKey(prev => prev + 1); // Increment key for new image
        setImageToCrop(imageDataUrl);
        setShowCropper(true);
        setPreview(null); // Clear any existing preview
        setSelectedFile(null); // Clear any existing file
        setHasProcessedInitialFile(true);
      };
      reader.onerror = () => {
        alert('Failed to load image. Please try again.');
      };
      reader.readAsDataURL(initialFile);
    }
  }, [isOpen, initialFile, hasProcessedInitialFile, imageToCrop]);

  // Reset all states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasProcessedInitialFile(false);
      setShowCropper(false);
      setImageToCrop(null);
      setSelectedFile(null);
      setPreview(null);
      setIsLoadingNewImage(false);
      setImageKey(0); // Reset key when modal closes
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      // If no file selected, clear loading state
      setIsLoadingNewImage(false);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      setIsLoadingNewImage(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      setIsLoadingNewImage(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // IMPORTANT: Keep hasProcessedInitialFile as true to prevent initialFile useEffect from re-triggering
    // This prevents the old initialFile from overwriting the new selection
    setHasProcessedInitialFile(true);

    // Clear any remaining states (cropper should already be cleared from handleChangeClick)
    setSelectedFile(null);
    setPreview(null);
    
    // Clear file input to remove previous selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Create preview and update cropper with new image
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageDataUrl = e.target.result;
      
      // Clear loading state first
      setIsLoadingNewImage(false);
      
      // Increment key to force component re-render
      setImageKey(prev => prev + 1);
      
      // Set new image and show cropper
      setImageToCrop(imageDataUrl);
      setShowCropper(true);
    };
    reader.onerror = () => {
      setIsLoadingNewImage(false);
      setShowCropper(false);
      setImageToCrop(null);
      alert('Failed to load image. Please try again.');
      // Clear file input on error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handleChangeClick = () => {
    // NEW APPROACH: Clear current cropper and show loading state
    // This ensures no previous selection is shown while waiting for new image
    setShowCropper(false);
    setImageToCrop(null);
    setSelectedFile(null);
    setPreview(null);
    // IMPORTANT: Keep hasProcessedInitialFile as true to prevent initialFile from re-processing
    setHasProcessedInitialFile(true);
    setIsLoadingNewImage(true); // Show loading state
    
    // Reset file input and trigger file selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      // Open file picker - loading state will be shown
      fileInputRef.current.click();
    }
  };

  const handleCropComplete = (croppedFile) => {
    setSelectedFile(croppedFile);
    setShowCropper(false);
    
    // Create preview of cropped image
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(croppedFile);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setSelectedFile(null);
    setPreview(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = () => {
    if (selectedFile && onChangeImage) {
      onChangeImage(selectedFile);
      // Don't reset preview immediately - let the upload progress show
      // The parent component will handle closing the modal after successful upload
    }
  };

  const handleRemove = () => {
    if (window.confirm(`Are you sure you want to remove this ${imageType === 'profile' ? 'profile picture' : 'banner image'}?`)) {
      if (onRemoveImage) {
        onRemoveImage();
      }
      onClose();
    }
  };

  // Only show displayImage (preview or original) when cropper is NOT active
  // Priority: cropper > preview > original imageUrl
  const hasActiveCropper = showCropper && imageToCrop;
  const displayImage = hasActiveCropper ? null : (preview || imageUrl);
  const isProfile = imageType === 'profile';

  return (
    <div className="image-view-modal-overlay" onClick={onClose}>
      <div className="image-view-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button 
          className="image-view-modal-close"
          onClick={onClose}
          title="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Image Display Area */}
        <div className={`image-view-modal-body ${showCropper ? 'has-cropper' : ''}`}>
          {isLoadingNewImage ? (
            <div className="image-cropper-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', flexDirection: 'column', gap: '16px' }}>
              <div style={{ textAlign: 'center', color: '#6b7280' }}>
                <div style={{ marginBottom: '10px', fontSize: '16px', fontWeight: '500' }}>Select a new image</div>
                <div style={{ fontSize: '14px', color: '#9ca3af' }}>Choose an image from your device</div>
              </div>
            </div>
          ) : showCropper && imageToCrop ? (
            <div className="image-cropper-section" key={`cropper-wrapper-${imageKey}`}>
              <ImageCropper
                key={`cropper-${imageKey}`} // Force re-render when key changes
                imageSrc={imageToCrop}
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
                onChangeImage={handleChangeClick}
                cropShape={isProfile ? 'round' : 'rect'}
                aspect={isProfile ? 1 : 4 / 1}
                imageType={imageType}
              />
            </div>
          ) : displayImage ? (
            <div className={`image-view-display ${isProfile ? 'profile-image round' : 'banner-image'} ${isProfile && selectedFile ? 'cropped-preview-round' : ''}`}>
              <img 
                src={displayImage} 
                alt={isProfile ? 'Profile' : 'Banner'}
                className={`image-view-large ${isProfile ? 'round-preview' : ''}`}
              />
            </div>
          ) : (
            <div className="image-view-placeholder">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {isProfile ? (
                  <>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </>
                ) : (
                  <>
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21,15 16,10 5,21"></polyline>
                  </>
                )}
              </svg>
              <p>No {isProfile ? 'profile picture' : 'banner image'} available</p>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && !showCropper && (
            <div className="image-view-upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <span className="progress-text">Uploading... {Math.round(uploadProgress)}%</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="image-view-modal-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {!showCropper && (
            <>
              {selectedFile && !isUploading && (
                <div className="image-view-preview-notice">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14,2 14,8 20,8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10,9 9,9 8,9"></polyline>
                  </svg>
                  <span>Image cropped and ready. Click "Upload" to save changes.</span>
                </div>
              )}

              <div className="image-view-buttons">
                <button
                  className="image-view-btn image-view-btn-change"
                  onClick={handleChangeClick}
                  disabled={isUploading}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                  </svg>
                  {selectedFile ? 'Select Different' : 'Change Image'}
                </button>

                {selectedFile && !isUploading && (
                  <button
                    className="image-view-btn image-view-btn-upload"
                    onClick={handleUpload}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17,8 12,3 7,8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <span>Upload</span>
                    
                  </button>
                )}

                {displayImage && !isUploading && !selectedFile && (
                  <button
                    className="image-view-btn image-view-btn-remove"
                    onClick={handleRemove}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3,6 5,6 21,6"></polyline>
                      <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                    Remove
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageViewModal;

